import React, { FunctionComponent } from 'react';
import withStyles from 'react-jss';
import { isNil } from 'ramda';
import { Grid, Statistic } from 'semantic-ui-react';
import { FormattedDate, FormattedNumber } from 'react-intl';
import classnames from 'classnames';

import FormattedCurrency from 'scenes/Report/components/FormattedCurrency';
import { ClassProp } from 'helpers/types';

import styles from './PerformanceOverviewChart.styles';
import {
  MerchantTransactionTotals,
  MerchantTransactionTotalsBySegment,
} from '../../PerformanceOverview.helper';

type Styles = ClassProp<ReturnType<typeof styles>>;

type ExtraClasses = {
  batchMode?: boolean;
};

type Props = {
  merchantTransactionTotals:
    | MerchantTransactionTotals
    | MerchantTransactionTotalsBySegment;
  classes: Styles;
  extraClasses: ExtraClasses;
};

const cashbackPercentageLabel = (
  cashbackPercentage: number,
  classes: Styles,
): JSX.Element => (
  <Statistic.Label
    className={classnames(classes.label, classes.noMargin, 'white', 'center')}
  >
    <p>
      {isNil(cashbackPercentage)
        ? 'Combined Report'
        : `${cashbackPercentage}% Cash Back`}
    </p>
  </Statistic.Label>
);

const genericLabel = (value: string, classes: Styles): JSX.Element => (
  <Statistic.Label
    className={classnames(classes.label, classes.noMargin, 'white', 'center')}
  >
    <p>{value ? value : 'Combined Report'}</p>
  </Statistic.Label>
);

const renderLabel = (
  totals: MerchantTransactionTotals | MerchantTransactionTotalsBySegment,
  classes: Styles,
) => {
  if ('cashbackPercentage' in totals) {
    return cashbackPercentageLabel(totals.cashbackPercentage, classes);
  }
  if ('segmentId' in totals) {
    return genericLabel(totals.segmentId, classes);
  }
};

const PerformanceOverviewChart: FunctionComponent<Props> = ({
  merchantTransactionTotals,
  classes,
  extraClasses,
}) => {
  const {
    numberOfTransactions,
    avgAov,
    totalTransactionAmount,
    roas,
    startDate,
    endDate,
  } = merchantTransactionTotals;

  return (
    <Grid
      className={classnames(classes.charts, {
        batchMode: extraClasses.batchMode,
      })}
    >
      <Grid.Row centered>
        <Grid.Column
          textAlign="center"
          className={classes.topHighlightedBlock}
          width={12}
        >
          <Statistic size="tiny">
            <Statistic.Value
              className={classnames(classes.value, 'white', 'center')}
            >
              Performance Report
            </Statistic.Value>
            {renderLabel(merchantTransactionTotals, classes)}
            <Statistic.Label
              className={classnames(
                classes.label,
                classes.noMargin,
                'white',
                'center',
              )}
            ></Statistic.Label>
          </Statistic>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row centered className={classes.noPadding}>
        <Grid.Column className={classnames(classes.leftBlock)} width={6}>
          <Statistic size="small" floated="left">
            <Statistic.Value className={classnames(classes.value)}>
              <FormattedNumber maximumFractionDigits={0} value={roas} />X
            </Statistic.Value>
            <Statistic.Label className={classes.label}>ROAS</Statistic.Label>
          </Statistic>
        </Grid.Column>

        <Grid.Column
          textAlign="center"
          className={classes.rightBlock}
          width={6}
        >
          <Statistic size="small">
            <Statistic.Value className={classes.value}>
              <FormattedNumber value={numberOfTransactions} />
            </Statistic.Value>
            <Statistic.Label className={classes.label}>
              # of Transactions
            </Statistic.Label>
          </Statistic>
        </Grid.Column>
      </Grid.Row>

      <Grid.Row centered className={classes.noPadding}>
        <Grid.Column
          className={classnames(classes.leftBlock, 'bottom')}
          width={6}
        >
          <Statistic size="small" floated="left">
            <Statistic.Value className={classes.value}>
              <FormattedCurrency value={avgAov} />
            </Statistic.Value>
            <Statistic.Label className={classes.label}>AOV</Statistic.Label>
          </Statistic>
        </Grid.Column>

        <Grid.Column
          className={classnames(classes.rightBlock, 'bottom')}
          width={6}
        >
          <Statistic size="small">
            <Statistic.Value className={classnames(classes.value, 'dateRange')}>
              <FormattedDate value={startDate} month="numeric" day="2-digit" />

              <span> to </span>

              <FormattedDate value={endDate} month="numeric" day="2-digit" />
            </Statistic.Value>

            <Statistic.Label className={classes.label}>
              Time Frame
            </Statistic.Label>
          </Statistic>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row centered>
        <Grid.Column
          textAlign="center"
          className={classes.highlightedBlock}
          width={12}
        >
          <Statistic size="small">
            <Statistic.Value
              className={classnames(classes.value, 'green', 'center')}
            >
              <FormattedCurrency value={totalTransactionAmount} />
            </Statistic.Value>

            <Statistic.Label
              className={classnames(
                classes.label,
                classes.noMargin,
                'white',
                'center',
              )}
            >
              Sales
            </Statistic.Label>
          </Statistic>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default withStyles(styles)(PerformanceOverviewChart);
